import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Search, Zap, Truck, Shield, Heart, Package, Wind, Flame, BookOpen, Star, ChevronRight, Lock, Sparkles, Cpu, Brain, Sun, X } from '../components/CustomIcons';

const NEW_COURSES = [
  { key: 'robotics', title: 'Consumer AI-Robotics Repair', hours: 40, credential: 'CAIR Cert.', Icon: Cpu, color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
  { key: 'agentic', title: 'Agentic Modeling', hours: 30, credential: 'Agentic Systems Assoc.', Icon: Brain, color: '#8B5CF6', bg: 'rgba(139,92,246,0.12)' },
  { key: 'solar', title: 'Intermediate PV Installation', hours: 45, credential: 'NABCEP PV Assoc.', Icon: Sun, color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
];

const SECTOR_ICONS = { 'Skilled Trades': Zap, 'Logistics': Truck, 'Technology': Shield, 'Healthcare': Heart, 'Food Service': BookOpen, 'Business': BookOpen, 'Academic': BookOpen, 'Universal Skills': Star };
const SECTORS = ['All', 'Skilled Trades', 'Technology', 'Healthcare', 'Logistics', 'Food Service', 'Business', 'Academic', 'Universal Skills'];

function PathwayCard({ pathway, onClick }) {
  const Icon = SECTOR_ICONS[pathway.sector] || BookOpen;
  const progress = pathway.user_progress || 0;
  const enrolled = pathway.enrolled || false;

  return (
    <div className="card" style={{ padding: '20px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '12px', transition: 'all 0.3s ease' }}
      onClick={() => onClick(pathway)}
      data-testid={`pathway-card-${pathway.id}`}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.5)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)'; }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={20} color="#F97316" />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', margin: '0 0 4px 0' }}>{pathway.name}</h3>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>{pathway.soc_code} | {pathway.growth_rate} Growth | {pathway.wage_range}</p>
        </div>
        {enrolled && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22C55E', flexShrink: 0 }} title="Enrolled" />}
      </div>

      <p style={{ fontSize: '13px', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif', margin: 0, lineHeight: 1.5 }}>{pathway.description}</p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {(pathway.certifications || []).slice(0, 3).map(c => (
          <span key={c} className="badge-grey" style={{ fontSize: '11px' }}>{c}</span>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{pathway.total_courses} courses</span>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>~{pathway.total_hours}hrs</span>
          <span style={{ fontSize: '12px', color: '#F97316' }}>★ {pathway.rating}</span>
        </div>
        {enrolled ? (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', color: '#F97316', fontWeight: 600, marginBottom: '4px' }}>{progress}% complete</div>
            <div className="progress-bar" style={{ width: '80px' }}>
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <button className="btn-primary" style={{ fontSize: '13px', padding: '7px 14px' }}>Start →</button>
        )}
      </div>
    </div>
  );
}

export default function Learn() {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [pathways, setPathways] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [sector, setSector] = useState('All');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null);
  const [newCoursesOpen, setNewCoursesOpen] = useState(
    () => localStorage.getItem('aos_new_courses_dismissed') !== '1'
  );

  // Clear the sidebar Learn badge when this page mounts
  useEffect(() => {
    localStorage.setItem('aos_learn_new_badge', '0');
  }, []);

  useEffect(() => {
    authFetch('/courses/pathways')
      .then(r => { setPathways(r.data); setFiltered(r.data); })
      .catch(e => console.error('load-pathways error:', e))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let results = pathways;
    if (sector !== 'All') results = results.filter(p => p.sector === sector);
    if (search) results = results.filter(p =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.description?.toLowerCase().includes(search.toLowerCase()) ||
      p.sector?.toLowerCase().includes(search.toLowerCase())
    );
    setFiltered(results);
  }, [sector, search, pathways]);

  const handlePathwayClick = async (pathway) => {
    if (!pathway.enrolled && user?.role === 'learner') {
      setEnrolling(pathway.id);
      try {
        await authFetch(`/courses/enroll/${pathway.id}`, { method: 'post' });
        setPathways(prev => prev.map(p => p.id === pathway.id ? { ...p, enrolled: true } : p));
      } catch (e) { console.error('enroll-pathway error:', e); }
      setEnrolling(null);
    }
    window.location.href = `/learn/${pathway.id}`;
  };

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="learn-page">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)', margin: '0 0 6px 0' }}>Career Pathways</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', fontFamily: 'Inter, sans-serif', margin: 0 }}>Every course leads to a credential. Every credential leads to a career.</p>
        </div>

        {/* ── What's New Banner ── */}
        {newCoursesOpen && (
          <div
            data-testid="learn-new-courses-banner"
            style={{
              marginBottom: '24px', borderRadius: '16px', overflow: 'hidden',
              background: 'linear-gradient(130deg, #0d1117 0%, #111827 100%)',
              border: '1px solid rgba(249,115,22,0.28)',
              boxShadow: '0 0 0 1px rgba(249,115,22,0.08), 0 4px 24px rgba(0,0,0,0.3)',
              position: 'relative',
            }}
          >
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 80% at 0% 50%, rgba(249,115,22,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ padding: '16px 20px', position: 'relative' }}>
              {/* Top row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <Sparkles size={14} color="#F97316" />
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '12px', color: '#F97316', textTransform: 'uppercase', letterSpacing: '0.12em' }}>What&apos;s New</span>
                <span style={{ marginLeft: '4px', width: '6px', height: '6px', borderRadius: '50%', background: '#F97316', animation: 'pulse 1.8s ease-in-out infinite', display: 'inline-block' }} />
                <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>Workforce pathways · Just launched</span>
                <button
                  onClick={() => { setNewCoursesOpen(false); localStorage.setItem('aos_new_courses_dismissed', '1'); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: '2px', lineHeight: 0, marginLeft: '8px' }}
                  aria-label="Dismiss new courses banner"
                  data-testid="learn-new-courses-dismiss"
                >
                  <X size={14} />
                </button>
              </div>
              {/* Course cards row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                {NEW_COURSES.map((c) => (
                  <button
                    key={c.key}
                    data-testid={`learn-new-course-${c.key}`}
                    onClick={() => {
                      try { localStorage.setItem('assistivos_active_role', 'professional_learner'); localStorage.setItem('assistivos_demo_role', 'professional_learner'); } catch (_e) {}
                      navigate('/prototype');
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(255,255,255,0.08)`,
                      borderRadius: '12px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px',
                      cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.18s, background 0.18s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = c.color; e.currentTarget.style.background = c.bg; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                  >
                    <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <c.Icon size={18} color={c.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                        <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px', color: '#fff', lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.title}</span>
                        <span style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#F97316', background: 'rgba(249,115,22,0.15)', borderRadius: '4px', padding: '2px 5px', flexShrink: 0, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>NEW</span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>{c.hours} hrs · {c.credential}</div>
                    </div>
                  </button>
                ))}
              </div>
              {/* CTA */}
              <button
                onClick={() => {
                  try { localStorage.setItem('assistivos_active_role', 'professional_learner'); localStorage.setItem('assistivos_demo_role', 'professional_learner'); } catch (_e) {}
                  navigate('/prototype');
                }}
                data-testid="learn-explore-pro-btn"
                style={{ background: '#F97316', color: '#09152A', border: 'none', borderRadius: '9px', padding: '8px 20px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, cursor: 'pointer' }}
              >
                Explore in Simulator →
              </button>
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search pathways, courses, or skills..."
                aria-label="Search pathways, courses, or skills"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '10px', color: 'var(--text-primary)', padding: '10px 12px 10px 38px', width: '100%', fontFamily: 'Inter, sans-serif', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = 'var(--orange)'}
                onBlur={e => e.target.style.borderColor = 'var(--border-default)'}
                data-testid="learn-search"
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
            {SECTORS.map(s => (
              <button key={s} onClick={() => setSector(s)} data-testid={`sector-filter-${s}`}
                style={{ padding: '6px 14px', borderRadius: '20px', border: sector === s ? '1px solid #F97316' : '1px solid #333333', background: sector === s ? 'rgba(249,115,22,0.15)' : 'transparent', color: sector === s ? '#F97316' : '#999999', fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' }}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', fontFamily: 'Inter, sans-serif', margin: 0 }}>{filtered.length} pathway{filtered.length !== 1 ? 's' : ''} available</p>
          <div className="badge-orange" style={{ fontSize: '11px' }}>★ Second-chance certified employers</div>
        </div>

        {/* Pathway Grid */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {[...Array(9)].map((_, i) => <div key={i} className="skeleton" style={{ height: '200px' }} />)}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {filtered.map(p => <PathwayCard key={p.id} pathway={p} onClick={handlePathwayClick} />)}
          </div>
        )}

        {filtered.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
            <Search size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
            <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '18px', color: 'var(--text-muted)' }}>No pathways found</p>
            <p style={{ fontSize: '14px' }}>Try a different search or filter</p>
          </div>
        )}
      </div>
    </div>
  );
}
